import { api } from './client';

export type ExtractionDocumentType = 'CCCD_FRONT' | 'CCCD_BACK' | 'LAND_TITLE' | 'OFFTAKE_CONTRACT';
export type ExtractionJobStatus = 'PENDING' | 'PROCESSING' | 'DONE' | 'FAILED';

/** A deterministic check the backend ran on the reading (MRZ check digits,
 * CCCD number vs birth year, contract dates…). */
export interface ExtractionCheck {
  code: string;
  ok: boolean;
  label: string;
}

export interface ExtractionJob {
  _id: string;
  fileId: string;
  documentType: ExtractionDocumentType;
  status: ExtractionJobStatus;
  fields?: Record<string, unknown>;
  confidence?: number;
  checks?: ExtractionCheck[];
  /** 'AI' = the document was actually read; 'MOCK' = no AI configured on the
   * server, fields are fixed samples. */
  source: 'AI' | 'MOCK' | string;
  errorMessage?: string;
}

/**
 * Starts reading a document and returns immediately at PENDING — never await
 * this and then block a form on it. Poll `getExtractionJob` instead (see
 * `pollExtractionJob`) so the rest of the form stays usable while it runs.
 * Only the uploader of a file can have it read.
 */
export async function createExtractionJob(fileId: string, documentType: ExtractionDocumentType): Promise<ExtractionJob> {
  return api.post<ExtractionJob>('/ai-extraction/jobs', { fileId, documentType });
}

export async function getExtractionJob(jobId: string): Promise<ExtractionJob> {
  return api.get<ExtractionJob>(`/ai-extraction/jobs/${jobId}`);
}

/** Polls until DONE/FAILED or `timeoutMs` elapses. A real AI reading takes
 * 5-30s (a multi-page contract PDF the longest). */
export async function pollExtractionJob(jobId: string, onUpdate?: (job: ExtractionJob) => void, timeoutMs = 120_000, intervalMs = 1500): Promise<ExtractionJob> {
  const startedAt = Date.now();
  for (;;) {
    const job = await getExtractionJob(jobId);
    onUpdate?.(job);
    if (job.status === 'DONE' || job.status === 'FAILED') return job;
    if (Date.now() - startedAt > timeoutMs) return { ...job, status: 'FAILED', errorMessage: 'AI đọc tài liệu quá lâu — vui lòng tự nhập hoặc thử lại.' };
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
}

/** create + poll in one call; never throws — a failure comes back as a FAILED job. */
export async function extractDocument(fileId: string, documentType: ExtractionDocumentType): Promise<ExtractionJob> {
  try {
    const job = await createExtractionJob(fileId, documentType);
    return await pollExtractionJob(job._id);
  } catch (e) {
    return {
      _id: '',
      fileId,
      documentType,
      status: 'FAILED',
      source: '',
      errorMessage: e instanceof Error && e.message ? e.message : 'Không đọc được tài liệu — vui lòng tự nhập.',
    };
  }
}

/** Labels for the fields worth showing, in display order. Anything else the
 * backend returns (side, mrzVerified, raw numbers) stays out of the list. */
export const EXTRACTION_FIELD_LABELS: Record<ExtractionDocumentType, Array<[string, string]>> = {
  CCCD_FRONT: [
    ['fullName', 'Họ tên'], ['cccd', 'Số CCCD'], ['dob', 'Ngày sinh'], ['gender', 'Giới tính'],
    ['placeOfOrigin', 'Quê quán'], ['address', 'Nơi thường trú'], ['issueDate', 'Ngày cấp'], ['expiryDate', 'Có giá trị đến'],
  ],
  CCCD_BACK: [],
  LAND_TITLE: [
    ['documentTitle', 'Loại giấy tờ'], ['documentNumber', 'Số hồ sơ'], ['name', 'Tên thửa'], ['code', 'Mã thửa'], ['address', 'Địa chỉ'],
    ['areaM2', 'Diện tích (m²)'], ['areaHa', 'Diện tích (ha)'], ['soilType', 'Mục đích sử dụng'], ['ownerName', 'Người sử dụng'],
    ['certificateNumber', 'Số GCN'], ['description', 'Mô tả'],
  ],
  OFFTAKE_CONTRACT: [
    ['effectiveDate', 'Hiệu lực'], ['expiryDate', 'Hết hạn'], ['cropSummary', 'Cây trồng'], ['valueVnd', 'Giá trị HĐ (đ)'],
    ['expectedDeliveryDate', 'Ngày giao'],
  ],
};
EXTRACTION_FIELD_LABELS.CCCD_BACK = EXTRACTION_FIELD_LABELS.CCCD_FRONT;

/** dd/mm/yyyy (what extraction returns) → yyyy-mm-dd (what <input type="date"> takes). */
export function vnDateToIso(v: unknown): string | undefined {
  const m = typeof v === 'string' ? /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v) : null;
  return m ? `${m[3]}-${m[2]}-${m[1]}` : undefined;
}

export const fieldString = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : typeof v === 'number' ? String(v) : undefined);
export const fieldNumber = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);

/**
 * The one crop a season form should take from a contract AI read: the first
 * crop line that matches the crop catalog, with that line's own quantity and
 * variety — not the contract's total across every crop.
 */
export function seasonCropFromContract(
  fields: Record<string, unknown>,
  catalog: string[],
  match: (text: string | undefined, names: string[]) => string | undefined,
): { crop?: string; outputKg?: number } {
  const lines = Array.isArray(fields.crops) ? (fields.crops as Array<Record<string, unknown>>) : [];
  for (const line of lines) {
    const crop = match(fieldString(line.crop), catalog);
    if (crop) return { crop, outputKg: fieldNumber(line.expectedOutputKg) };
  }
  return { crop: match(fieldString(fields.mainCrop), catalog), outputKg: fieldNumber(fields.expectedOutputKg) };
}
